import '../../i18n/config';
import { useEffect, useMemo, useState } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';

import Header from 'components/header';

import { IPermissions, useUserData } from 'hooks/useUserData';
import { useArtistProfiles } from 'hooks/swrRequests/useArtistProfiles';

import api from 'utils/api';
import { rememberRequestedUrl } from 'utils/postLoginRedirect';

import { authorizedRoutes, unauthorizedRoutes } from './routes';
import CookieBanner from '../../components/cookie-banner';
import { Box } from '@mui/material';
import { WithTranslation, withTranslation } from 'react-i18next';
import React from 'react';

/** Paths a user locked in onboarding can still visit. */
const LOCKED_ALLOWED_PATHS = ['/', '/contact', '/profile', '/account', '/new-password'];

/** Switch fallback: an unauthenticated visit to a protected URL (e.g. a shared /tour-budget#t=… link) is remembered so sign-in can land there. */
const FallbackRedirect: React.FC<{ to: string; remember: boolean }> = ({ to, remember }) => {
  const location = useLocation();
  if (remember) rememberRequestedUrl(location);
  return <Redirect to={to} />;
};

const App: React.FC<WithTranslation> = ({ t }) => {
  const [loading, setLoading] = useState(true);
  const { userData, updateUserData, updatePermissionData, onboardingInProgress } = useUserData();

  // The sidebar/app unlocks once at least one artist profile is complete.
  const { anyCompleted, isLoading: profilesLoading } = useArtistProfiles(!onboardingInProgress);
  const isOnboardingLocked = onboardingInProgress && !anyCompleted;

  useEffect(() => {
    const accessToken = localStorage.getItem('Authorization');

    const fetchUserData = async () => {
      try {
        const data = await api.auth.getProfile();
        updateUserData({ ...data, isAuthorized: true });

        const permissions: IPermissions = await api.auth.getPermissions();
        updatePermissionData(permissions);
      } catch (err: any) {
        if (err?.status === 403) return;
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchUserData().catch(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
      updateUserData({ isAuthorized: false });
    }
  }, [updateUserData, updatePermissionData]);

  /*
    useEffect(() => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        window.$crisp = [];
        window.CRISP_WEBSITE_ID = '2e7840ee-2d6f-400d-9c0f-4c9a10d1ffb2';
        (function () {
          var d = document;
          var s = d.createElement('script');
          s.src = 'https://client.crisp.chat/l.js';
          s.async = 1;
          d.getElementsByTagName('head')[0].appendChild(s);
        })();
      `;

      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }, []);
  */

  const currentRoutes = useMemo(() => {
    if (!userData.isAuthorized) return unauthorizedRoutes;
    if (isOnboardingLocked) {
      return authorizedRoutes.filter(({ path }) => LOCKED_ALLOWED_PATHS.includes(path));
    }
    return authorizedRoutes;
  }, [userData, isOnboardingLocked]);

  return (
    <>
      <Header />
      {loading || (onboardingInProgress && profilesLoading) ? (
        <Box
          bgcolor="#000"
          color="#ddd"
          fontSize="22px"
          textAlign="center"
          sx={{
            height: 'calc(100vh - 72px)',
          }}
        >
          <Box paddingTop={10}>{t('Loading....')}</Box>
        </Box>
      ) : (
        <Switch>
          {currentRoutes.map(({ path, component, exact = false }) => (
            <Route key={path} path={path} exact={exact} component={component} />
          ))}

          <FallbackRedirect
            to={isOnboardingLocked ? '/profile' : '/'}
            remember={!userData.isAuthorized}
          />
        </Switch>
      )}
      <CookieBanner />
    </>
  );
};

export default withTranslation()(App);
